import React, { useEffect, useState } from "react";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";
import { toast } from "sonner";

/**
 * MobileAppInstallBanner
 * --------------------------------------------------------------
 * A dismissable, sticky "Install to home screen" banner shown at
 * the very top of `/app` on any device that has NOT already
 * installed EVIQ as a standalone PWA.
 *
 * • Chrome/Edge/Android with a deferred prompt → fires native install
 * • iOS Safari → opens 3-step "Share → Add to Home Screen" instructions
 * • Every other case → shows the same iOS-style tap guide (safe default)
 * • Already installed (display-mode: standalone) → renders nothing
 * • User dismissed this session → hidden until page reload
 */
export const MobileAppInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(() => window.__eviqInstallPrompt || null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(
    () => { try { return localStorage.getItem("eviq_pwa_installed") === "1"; } catch (e) { return false; } }
  );

  useEffect(() => {
    const ua = navigator.userAgent || "";
    setIsIOS(/iPad|iPhone|iPod/.test(ua) && !window.MSStream);

    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);
    // Running standalone proves the app is installed — remember it so the
    // banner also stays hidden in regular browser tabs on this device.
    if (standalone) {
      try { localStorage.setItem("eviq_pwa_installed", "1"); } catch (e) {}
    }
    // Android Chrome can report installed PWAs directly in browser tabs.
    if (!standalone && navigator.getInstalledRelatedApps) {
      navigator.getInstalledRelatedApps().then((apps) => {
        if (apps && apps.length > 0) {
          setInstalled(true);
          try { localStorage.setItem("eviq_pwa_installed", "1"); } catch (e) {}
        }
      }).catch(() => {});
    }

    setDismissed(sessionStorage.getItem("eviq_install_banner_dismissed") === "1");

    // The prompt is captured globally in index.js (it usually fires before
    // this component mounts). These events keep us in sync either way.
    const onReady = () => setDeferredPrompt(window.__eviqInstallPrompt || null);
    const onInstalled = () => {
      setIsStandalone(true);
      setInstalled(true);
      try { localStorage.setItem("eviq_pwa_installed", "1"); } catch (e) {}
      toast.success("EVIQ installed — the icon is on your home screen");
    };
    window.addEventListener("eviq:install-ready", onReady);
    window.addEventListener("eviq:app-installed", onInstalled);
    return () => {
      window.removeEventListener("eviq:install-ready", onReady);
      window.removeEventListener("eviq:app-installed", onInstalled);
    };
  }, []);

  if (isStandalone || installed || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem("eviq_install_banner_dismissed", "1");
    setDismissed(true);
  };

  const doInstall = async () => {
    const prompt = deferredPrompt || window.__eviqInstallPrompt;
    if (prompt) {
      // One press → native OS install dialog → icon lands on home screen.
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      window.__eviqInstallPrompt = null;
      setDeferredPrompt(null);
      if (outcome === "accepted") {
        setIsStandalone(true);
        try { localStorage.setItem("eviq_pwa_installed", "1"); } catch (e) {}
      }
      return;
    }
    // iOS Safari (Apple blocks programmatic installs) + other browsers →
    // guided steps.
    setShowHelp(true);
  };

  return (
    <>
      <div
        data-testid="mobile-app-install-banner"
        className="sticky top-0 z-40 border-b border-[#39FF14]/25 bg-black/85 backdrop-blur-md"
      >
        <div className="max-w-3xl mx-auto px-3 py-2 flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: "rgba(57,255,20,0.10)", border: "1px solid rgba(57,255,20,0.35)" }}
            aria-hidden="true"
          >
            <Smartphone className="w-4 h-4" style={{ color: "#39FF14" }} strokeWidth={2.2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-semibold text-white leading-tight truncate">
              Install EVIQ as an app
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.14em] text-zinc-500 leading-tight mt-0.5">
              {deferredPrompt ? "One press · icon on your home screen" : "One-tap access · Offline-ready · No app store"}
            </div>
          </div>
          <button
            type="button"
            onClick={doInstall}
            data-testid="mobile-install-cta"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#39FF14] text-black text-[12px] font-semibold px-3 py-1.5 hover:opacity-90 transition-opacity"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2.6} aria-hidden="true" />
            Install
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss install banner"
            data-testid="mobile-install-dismiss"
            className="shrink-0 p-1.5 rounded-full text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </div>

      {showHelp && <InstallHelpSheet isIOS={isIOS} onClose={() => setShowHelp(false)} />}
    </>
  );
};

const InstallHelpSheet = ({ isIOS, onClose }) => (
  <div
    className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md flex items-end sm:items-center justify-center px-4"
    data-testid="mobile-install-help-sheet"
    role="dialog"
    aria-modal="true"
    onClick={onClose}
  >
    <div
      className="relative w-full max-w-sm rounded-3xl border border-white/10 bg-[#0a0a0a] p-5 text-white mb-3 sm:mb-0"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="mobile-install-help-close"
        className="absolute top-3 right-3 text-zinc-500 hover:text-white p-2 rounded-full border border-white/10 hover:border-white/25 transition-colors"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" />
      </button>

      <div className="mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1">
          Install EVIQ
        </div>
        <h3 className="text-base font-semibold leading-tight">
          {isIOS
            ? "Add EVIQ to your iPhone home screen"
            : "Add EVIQ to your home screen"}
        </h3>
      </div>

      <ol className="space-y-2.5 mb-3">
        <Step n="1">
          Tap the{" "}
          <strong className="text-white inline-flex items-center gap-1">
            Share
            <span
              className="inline-flex items-center justify-center rounded-md align-middle"
              style={{ width: 20, height: 20, background: "rgba(0,229,255,0.1)", border: "1px solid rgba(0,229,255,0.35)" }}
              aria-hidden="true"
            >
              <Share className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.2} />
            </span>
          </strong>{" "}
          icon in your browser.
        </Step>
        <Step n="2">
          Choose{" "}
          <strong className="text-white inline-flex items-center gap-1">
            Add to Home Screen
            <span
              className="inline-flex items-center justify-center rounded-md align-middle"
              style={{ width: 20, height: 20, background: "rgba(57,255,20,0.1)", border: "1px solid rgba(57,255,20,0.35)" }}
              aria-hidden="true"
            >
              <Plus className="w-3 h-3" style={{ color: "#39FF14" }} strokeWidth={2.5} />
            </span>
          </strong>
        </Step>
        <Step n="3">
          Tap <strong className="text-white">Add</strong>. EVIQ opens like a native app.
        </Step>
      </ol>

      <p className="text-[11px] text-zinc-500 leading-relaxed">
        {isIOS
          ? "Works in Safari on iPhone/iPad. Chrome on iOS can't install home-screen apps."
          : "In Chrome, tap the ⋮ menu → 'Install app' if the Share sheet doesn't show 'Add to Home Screen'."}
      </p>
    </div>
  </div>
);

const Step = ({ n, children }) => (
  <li className="flex gap-2.5 items-start rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2 text-[13px] text-zinc-300 leading-relaxed">
    <span
      className="shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-mono font-semibold"
      style={{ background: "rgba(0,229,255,0.12)", color: "#00E5FF", border: "1px solid rgba(0,229,255,0.4)" }}
      aria-hidden="true"
    >
      {n}
    </span>
    <span>{children}</span>
  </li>
);
